/**
 * Session state for one open Contact Builder lead form (RequestATourForm or
 * GetMoreInformationForm, both in components/). Module-level singleton — only one of the two
 * forms is ever mounted at a time (they share the same modal container), so there's never more
 * than one session.
 *
 * Not a Redux slice: this data is purely for tracking (NHS20-8253) and never drives UI, so it
 * doesn't belong in the store.
 */
const TRACKED_FIELD_NAMES = [
  'name', 'email', 'phone', 'postalCode', 'preferredContactMethod', 'realtorQuestion',
];

// Distinct from TRACKED_FIELD_NAMES (which getFieldsCompletedCount reads): 'date' is excluded
// there because DateCarousel's hidden input always has a default value, but it's still a
// legitimate field for interaction/last-field purposes (see requestATourForm.js's
// bindChangeEvents, which calls recordFieldInteraction('date') on an actual date pick).
const TRACKABLE_FIELD_NAMES = new Set([...TRACKED_FIELD_NAMES, 'date']);

const state = {
  buttonName: null,
  fromPage: null,
  openedAt: null,
  formStarted: false,
  submitAttempted: false,
  lastFieldInteracted: null,
  submitted: false,
  errorFieldNames: new Set(),
};

export function startLeadFormSession({ buttonName, fromPage }) {
  state.buttonName = buttonName;
  state.fromPage = fromPage;
  state.openedAt = Date.now();
  state.formStarted = false;
  state.submitAttempted = false;
  state.lastFieldInteracted = null;
  state.submitted = false;
  state.errorFieldNames = new Set();
}

/**
 * Ignores interactions on fields this flow doesn't track (e.g. the ZIP "Outside U.S." checkbox,
 * name="livesOutside") — only a tracked field can become formStarted/lastFieldInteracted, so
 * abandonment/validation analytics never report an untracked field name.
 * @param {string|null|undefined} fieldName
 */
export function recordFieldInteraction(fieldName) {
  if (!fieldName || !TRACKABLE_FIELD_NAMES.has(fieldName)) {
    return;
  }

  state.formStarted = true;
  state.lastFieldInteracted = fieldName;
}

export function recordValidationErrors(fieldNames) {
  state.errorFieldNames = new Set(fieldNames);
}

/**
 * Marks that the user clicked Submit and validation failed — distinct from `formStarted`
 * (`recordFieldInteraction`, focus-driven), since a submit can be attempted with zero fields ever
 * focused (e.g. prefilled fields left untouched, then Submit clicked immediately). Lets
 * trackLeadFormClosed treat a failed submit as the start of an abandonable session even when no
 * field was individually interacted with.
 */
export function markSubmitAttempted() {
  state.submitAttempted = true;
}

export function markSubmitted() {
  state.submitted = true;
}

export function getElapsedSeconds() {
  if (!state.openedAt) {
    return 0;
  }

  return Math.round((Date.now() - state.openedAt) / 1000);
}

/**
 * Counts how many of the tracked fields currently have a value, reading live from the DOM
 * (form.elements) rather than from state, so it reflects the form at the exact moment of close.
 * @param {HTMLFormElement|null} formElement
 * @returns {number}
 */
export function getFieldsCompletedCount(formElement) {
  if (!formElement) {
    return 0;
  }

  return TRACKED_FIELD_NAMES.filter((fieldName) => Boolean(formElement.elements.namedItem(fieldName)?.value)).length;
}

export function getLeadFormSessionSnapshot() {
  return { ...state, errorFieldNames: new Set(state.errorFieldNames) };
}

export function resetLeadFormSession() {
  state.buttonName = null;
  state.fromPage = null;
  state.openedAt = null;
  state.formStarted = false;
  state.submitAttempted = false;
  state.lastFieldInteracted = null;
  state.submitted = false;
  state.errorFieldNames = new Set();
}
